import { useCallback, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { t as translate } from 'i18next';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';
import * as yup from 'yup';

import fs from '@/components/atoms/Fieldset';
import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useDirtyFormBlocker } from '@/hooks/useDirtyFormBlocker';
import { usePreferences } from '@/hooks/usePreferences';
import { useResetFormOnAccountSwitch } from '@/hooks/useResetFormOnAccountSwitch';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';

type FormValues = { hours: number; minutes: number };

const FORM_ID = 'human-agents-form';
const ERROR_ID = 'human-agents-error';
const MIN_IDLE_MINUTES = 5;
const MAX_IDLE_HOURS = 4;

const totalIdleMinutes = (hours: number, minutes: number) => hours * 60 + minutes;

const hoursRangeError = () => translate('account.human_agents.hours_range');
const minutesRangeError = () => translate('account.human_agents.minutes_range');

export const formSchema = yup.object().shape({
  hours: yup
    .number()
    .integer()
    .min(0, hoursRangeError)
    .max(MAX_IDLE_HOURS, hoursRangeError)
    .test(
      'max-idle-total',
      () => translate('account.human_agents.error'),
      (hours = 0, { parent }) => totalIdleMinutes(hours, parent.minutes) <= MAX_IDLE_HOURS * 60
    ),
  minutes: yup
    .number()
    .integer()
    .min(0, minutesRangeError)
    .max(59, minutesRangeError)
    .test(
      'min-idle-total',
      () => translate('account.human_agents.min_idle'),
      (minutes = 0, { parent }) => totalIdleMinutes(parent.hours, minutes) >= MIN_IDLE_MINUTES
    ),
});

export const HumanAgents = () => {
  const { t } = useTranslation();
  const canUpdate = useSelector((state: RootState) =>
    getPermissions(state, 'accounts', actions.UPDATE)
  );
  const { accountPreferences, updateAccountPreferencesAsync } = usePreferences();
  const away_threshold = (accountPreferences?.away_threshold as number) ?? 20;
  const formMethods = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(formSchema) as Resolver<FormValues>,
    resetOptions: KEEP_DIRTY_VALUES,
    values: { hours: Math.floor(away_threshold / 60), minutes: away_threshold % 60 },
  });

  const {
    control,
    trigger,
    handleSubmit,
    getValues,
    formState: { isDirty, isSubmitting, errors },
  } = formMethods;

  const onSubmit = useCallback(
    async ({ hours, minutes }: FormValues) => {
      await updateAccountPreferencesAsync({ away_threshold: hours * 60 + minutes });
      formMethods.reset({ hours, minutes }, CLEAR_DIRTY_VALUES);
    },
    [formMethods, updateAccountPreferencesAsync]
  );

  const hours = useWatch({ control, name: 'hours' });
  const minutes = useWatch({ control, name: 'minutes' });

  useEffect(() => {
    trigger(['hours', 'minutes']);
  }, [hours, minutes, trigger]);

  useTitle(t('pages.human_agents'));
  useTrackFormState({ isDirty, formId: FORM_ID });
  useResetFormOnAccountSwitch(formMethods);

  // Throws FormValidationError on invalid input so the leave-guard modal stays on the page to fix it
  const save = useCallback(async () => {
    if (!(await trigger())) {
      const error = new Error('Form is invalid');
      error.name = 'FormValidationError';
      throw error;
    }
    await onSubmit(getValues());
  }, [trigger, getValues, onSubmit]);
  useDirtyFormBlocker(save);

  const toInt = (value: string) => (value === '' ? 0 : parseInt(value, 10));

  return (
    <SettingsContent className="flex flex-col gap-3">
      <fs.Title>{t('account.human_agents.title')}</fs.Title>
      <Form {...formMethods}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty && canUpdate}
            isLoading={isSubmitting}
            disabled={!isDirty || !canUpdate}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex items-start gap-6">
              <FormField
                control={control}
                name="hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.hours')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => field.onChange(toInt(e.target.value))}
                        aria-describedby={errors.hours ? ERROR_ID : undefined}
                        placeholder="0"
                        type="number"
                        size="sm"
                        min={0}
                        readOnly={!canUpdate}
                        className="w-20"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.minutes')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => field.onChange(toInt(e.target.value))}
                        aria-describedby={errors.minutes ? ERROR_ID : undefined}
                        placeholder="0"
                        type="number"
                        size="sm"
                        min={0}
                        readOnly={!canUpdate}
                        className="w-20"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            {errors.hours || errors.minutes ? (
              <div id={ERROR_ID} role="alert" className="mt-4 space-y-1 text-sm text-warning">
                {errors.hours?.message && <p>{errors.hours.message}</p>}
                {errors.minutes?.message && <p>{errors.minutes.message}</p>}
              </div>
            ) : (
              <p className="mt-4 text-sm text-foreground-muted">
                {t('account.human_agents.subtitle')}
              </p>
            )}
          </FieldsetCard>
        </form>
      </Form>
    </SettingsContent>
  );
};
